import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import Pagination from "components/Pagination";
import { useMediaQuery } from "hooks/utils/useMediaQuery";
import { useGetFaq } from "hooks/common/useGetFaq";
import { useGetFaqDetail } from "hooks/common/useGetFaqDetail";
import { api_uri } from "hooks/utils/enum";
import { ReactComponent as UpArrow } from "assets/morning/icon/upArrow.svg";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import { ReactComponent as ReadingGlasses } from "assets/common/common/readingGlasses.svg";
import speaker from "assets/common/support/notice_speaker.png";

export default function Faq({ type }: { type: string }) {
    const responsive700 = useMediaQuery(700);
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchForm, setSearchForm] = useState({
        category: type,
        search: "subject",
        searchtxt: "",
        page: 1,
        limit: 10,
    });

    const { data } = useGetFaq(searchForm);
    const [activeIdx, setActiveIdx] = useState<number | null>(null);
    const { data: faqDetail } = useGetFaqDetail({ category: type, list_no: activeIdx });

    useEffect(() => {
        const page = searchParams.get("page") ?? "1";
        if (page) {
            setSearchForm(prev => ({ ...prev, page: Number(page) }));
        }
    }, [searchParams]);

    useEffect(() => {
        setSearchForm(prev => ({
            ...prev,
            limit: responsive700 ? searchForm.limit / 2 : searchForm.limit,
        }));
    }, [responsive700]);

    return (
        <div
            className={`${responsive700 ? "bg-white" : "bg-[#f4f4f4]"} flex-1 flex flex-col items-center pb-16 pt-5 responsive1200:pt-[0px] responsive1200:px-[4%] responsive700:px-0`}
        >
            <p className="py-[35px] text-[#333] text-[28px] font-[600] responsive1200:py-[30px] responsive1200:text-[24px]">FAQ</p>
            <div
                className={`bg-white w-full max-w-[1200px] p-[50px] flex flex-col responsive1200:px-[4%] responsive700:px-0 responsive700:!pt-0`}
            >
                {responsive700 ? (
                    <div className="flex flex gap-[5px] responsive700:px-[4%]">
                        <div
                            className={`relative flex-1 flex items-center gap-[10px] border border-[#E1E2E4] rounded-[8px] overflow-hidden px-[10px] [&:has(input:focus)]:ring-2 ring-${type}`}
                        >
                            <input
                                type="text"
                                value={searchForm.searchtxt}
                                onChange={e => setSearchForm(prev => ({ ...prev, searchtxt: e.target.value, page: 1 }))}
                                placeholder="검색어를 입력해주세요."
                                className={`focus:!outline-${type} text-[16px] placeholder:text-[#878A93] w-full focus:outline-none py-[12px]`}
                            />
                            <ReadingGlasses />
                        </div>
                    </div>
                ) : (
                    <div className="responsive700:hidden border-t border-b border-[#dddddd] bg-[#f8f8f8] flex gap-[10px] p-[15px] mb-[30px]">
                        <Select value={searchForm.search} onValueChange={value => setSearchForm(prev => ({ ...prev, search: value }))}>
                            <SelectTrigger
                                className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-none focus:ring-2 focus:ring-${type} max-w-[150px] border border-[#E1E2E4] bg-white`}
                            >
                                <SelectValue placeholder="" />
                                <ArrowDown />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                <SelectItem value="subject">제목</SelectItem>
                                <SelectItem value="contents">내용</SelectItem>
                            </SelectContent>
                        </Select>

                        <div className="relative flex-1">
                            <input
                                type="text"
                                value={searchForm.searchtxt}
                                onChange={e => setSearchForm(prev => ({ ...prev, searchtxt: e.target.value, page: 1 }))}
                                placeholder="검색어를 입력해주세요."
                                className={`focus:outline-${type} text-[16px] placeholder:text-[#878A93] border border-[#E1E2E4] rounded-none w-full h-full px-[10px]`}
                            />
                        </div>
                    </div>
                )}

                <div className={`flex flex-col mb-[24px] mt-[12px] ${!responsive700 && "border-t-[3px] border-t-[#999999]"}`}>
                    <div>
                        {responsive700 && (
                            <ul className="px-[4%] flex mb-[12px]">
                                <li className="text-white bg-[#132E47] rounded-[8px] px-[14px] py-[7px]">전체</li>
                            </ul>
                        )}

                        {data?.data_list.length === 0 ? (
                            <p className="text-center py-[50px]">등록된 게시글이 없습니다.</p>
                        ) : (
                            <div className="border-t border-[#E1E2E4]">
                                {data?.data_list.map(item => {
                                    const isActive = activeIdx === item.list_no;
                                    return (
                                        <div
                                            key={item.list_no}
                                            className="bg-white flex flex-col border-b border-[#E1E2E4]"
                                            onClick={() => setActiveIdx(prev => (prev === item.list_no ? null : item.list_no))}
                                        >
                                            <div className="gap-[5px] px-[4%] py-[20px] items-start grid grid-cols-[50px_1fr_30px]">
                                                {item.notice === 1 ? <img src={speaker} alt="" /> : <span></span>}
                                                <p className={`${!isActive && "truncate"} max-w-[90%]`}>{item.subject}</p>
                                                <UpArrow
                                                    width={20}
                                                    height={20}
                                                    className={`shrink-0 transition-[transform] origin-center ${isActive ? "rotate-none" : "rotate-[-180deg]"}`}
                                                />
                                            </div>
                                            {activeIdx === item.list_no && (
                                                <div className="bg-[#FAFAFD] px-[4%] border-t border-[#E1E2E4]">
                                                    <div
                                                        className="py-12 text-[#666666]"
                                                        dangerouslySetInnerHTML={{ __html: faqDetail?.contents || "" }}
                                                    />
                                                    {faqDetail?.b_file && (
                                                        <div className="py-[20px] border-t border-[#E1E2E4] text-[15px] text-[#333333] flex gap-x-[50px] gap-y-[10px] flex-wrap">
                                                            <p className="text-nowrap">첨부파일</p>

                                                            <Link
                                                                to={`${api_uri}/upload/board/${faqDetail.b_file}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="hover:underline cursor-pointer break-all"
                                                            >
                                                                {faqDetail.b_file}
                                                            </Link>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <Pagination
                    current={searchForm.page}
                    total={data?.total_count || 1}
                    pageSize={responsive700 ? searchForm.limit / 2 : searchForm.limit}
                    groupSize={responsive700 ? searchForm.limit / 2 : searchForm.limit}
                    onChange={newPage => {
                        searchParams.set("page", String(newPage));
                        setSearchParams(searchParams);
                    }}
                />
            </div>
        </div>
    );
}
